<script>
    import { apnumber, pluralize, capfirst, apdate } from 'journalize';
    import { loadJsonScript } from '../utils/load-json-script';

    // import data for robotext
    // // topline numbers by city
    // const cityToplines = loadJsonScript('city-toplines');
    // ordinance details
    const ordinance = loadJsonScript('ordinances');
    // date details
    const update_dates = loadJsonScript('data_update_dates');
    // building category definitions
    const category_definitions = loadJsonScript('categories');


    export let data;
    
    let city = data.ordinance_city;
    let address = data.clean_address;


    function isoStringToDate(str) {
      const [year, month, day] = str.split('-');
      const date = new Date(year, month - 1, day);

      return apdate(date).replace(`, ${year}`, '');
    }

    let matched_update_date_str = update_dates[city].update_date;

    $: category = data.category;

    let all_ordinances = ordinance[city];
    $: ordinance_str = all_ordinances[category];
    let zone;
    let status;
    let building_type;

    let liquefaction_zone_definition = 'During an earthquake, shaking can cause land to behave like quicksand and the ground to fail. This can happen in places where the land is made of loose sand or silt and filled with groundwater.';

    let fault_zone_definition = 'If a fault ruptures during an earthquake, buildings straddling fault lines within that area could be torn apart depending on where the earthquake occurs and which way the earth moves.';

    if (data.is_liquefaction_zone == true && data.is_fault_zone == false) {
        // only liquefaction zone
        zone = `<div class="hazard-zones">
            <p>This building is in a <span class="highlight lz">liquefaction zone</span>. ${liquefaction_zone_definition}</p>
        </div>`;
    } else if (data.is_liquefaction_zone == true && data.is_fault_zone == true) {
        // both liquefaction and fault zone
        zone = `<div class="hazard-zones">
            <p>This building is in a <span class="highlight lz">liquefaction zone</span> and in the <span class="highlight fz">${data.fault_zone}</span>.</p>
            <p>${liquefaction_zone_definition}</p>
            <p>${fault_zone_definition}</p>
        </div>`;
    } else if (data.is_liquefaction_zone == false && data.is_fault_zone == true) {
        // only fault zone
        zone = `<div class="hazard-zones">
            <p>This building is in the <span class="highlight fz">${data.fault_zone}</span>. ${fault_zone_definition}</p>
        </div>`;
    } else if (data.is_liquefaction_zone == false && data.is_fault_zone == false) {
        // not in seismic hazard area
        zone = '';
    }

    let is_retrofitted = data.is_retrofitted;
    let incomplete_count = data.not_retrofitted;
    let complete_count = data.complete;

    if (is_retrofitted == 0) {
        // IF INCOMPLETE
        if ((incomplete_count == 1) & !(complete_count)) {
            // if there's just one record and it's not retrofitted
            status = `this address <b class="not_retrofitted">has not been retrofitted</b>.`
        } else if ( (incomplete_count >= 1) & !(complete_count) ) {
            // there's more than one incomplete record and no complete
            status = ` ${ apnumber(incomplete_count) } building${pluralize(incomplete_count)} or unit${pluralize(incomplete_count)} at this location <b class="not_retrofitted">ha${ pluralize(incomplete_count, 've', 's') } not been retrofitted</b>.`
        } else if ( (incomplete_count >= 1) & (complete_count == 1) ) {
            // there's at least one incomplete building and one complete
            status = ` this address <b class="not_retrofitted">has not been fully retrofitted</b>. ${ capfirst(apnumber(incomplete_count)) } building${pluralize(incomplete_count)} or unit${pluralize(incomplete_count)} at this location ${ pluralize(incomplete_count, 'are', 'is') } incomplete. ${ capfirst(apnumber(complete_count)) } other building${pluralize(complete_count)} or unit${pluralize(complete_count)} ha${ pluralize(complete_count, 've', 's') } completed construction.`
        } else if ( (incomplete_count >= 1) & (complete_count >= 1) ) {
            // there are incomplete and complete buildings
            status = ` this address <b class="not_retrofitted">has not been fully retrofitted</b>. ${ capfirst(apnumber(incomplete_count)) } building${pluralize(incomplete_count)} or unit${pluralize(incomplete_count)} at this location ${ pluralize(incomplete_count, 'are', 'is') } incomplete. Another ${ apnumber(complete_count) } unit${ pluralize(complete_count) } ha${ pluralize(complete_count, 've', 's') } completed construction.`
        } else {
            // fallback
            status = `this address <b class="not_retrofitted">has not been retrofitted</b>.`
        }
    } else if  (is_retrofitted == 1) {
        // IF COMPLETE
        status = `this address <b class="complete">has been retrofitted</b>.`
    } else {
        // IF ALL ELSE FAILS
        status = `the retrofit status is unknown.`
    }
    
    if (data.building_type) {
        building_type = data.building_type;
    } else {
        building_type = '';
    }

    function aOrAn(word) {
        // const vowels = ["a", "e", "i", "o", "u"];
        // const firstLetter = word.charAt(0).toLowerCase();

        const words = [
            'unknown',
            'apartment',
            'unreinforced masonry'
        ]

        if (words.includes(word)) {
            return "an";
        } else {
            return "a";
        }
    }
    // building_type = aOrAn(building_type) + " " + building_type;

    let building_type_list = [];
    Object.entries(all_ordinances).forEach( ([key, value]) => {
        if ((value) && (key != data.category)){
            building_type_list.push(key);
        }            
    })

    $: category_source = 'City records';

    $: ordinance_box_str = '';
    $: category_box = category;

    
</script>

<h3><b>{address}</b> is {aOrAn(building_type)} {building_type} building in the city of <b>{city}</b>. According to city records obtained by the Times on { isoStringToDate(matched_update_date_str) }, {@html status }</h3>

{#if category}
    <p class="building-category">{category_source} classify this address as {aOrAn(category)} <strong>{category} building</strong>.</p>
    <p class="building-category-definition">{category_definitions[category].definition }</p>
    <p class="ordinance">{@html ordinance_str }</p>
{:else}
    <p class="building-category">City records do not specify a building type for this address. If you know the construction style of your building, you can use the buttons below to find more information about local retrofitting ordinances.</p>
{/if}

{#if ['Los Angeles', 'Santa Monica', 'West Hollywood'].includes(city) }
    <aside class="infobox" id="building-category-toggle">
        <h4>Explore the other ordinances for your city</h4>
        <p>The city of {city} has {pluralize(building_type_list, "", "a")} recent ordinance{pluralize(building_type_list, "s", "")} on the books for { apnumber(building_type_list.length) } other type{pluralize(building_type_list.length)} of building. Click to see the rules.</p>
        
        <div class="buttons">
            {#each building_type_list as b}
            {#if b == category_box}
                <button disabled>
                    { capfirst(b)}
                </button>
            {:else}
                <button
                    on:click={() => ( 
                        ordinance_box_str = all_ordinances[b],
                        category_box = b
                    )}
                >
                    { capfirst(b)}
                </button>
            {/if}

            {/each}
        </div>
        <div id="ordinance-box">
            <p class="ordinance">{@html ordinance_box_str }</p>
        </div>
    </aside>
{/if}

{@html zone}